import { useQuery } from '@tanstack/react-query';
import { useRum } from 'common-rum';
import {
  createPreflightCoreApiClient,
  DocumentId,
  ExtendedSubmissionInfo,
  getExtendedSubmissionInfo,
  SubmissionState,
} from 'preflight-core';
import { useEffect, useState } from 'react';

import { preflightApiClient } from '../api/client';
import { Voucher } from '../vouchers/get-voucher';

const preflightCoreApiClient = createPreflightCoreApiClient(preflightApiClient);
const twoMinutes = 60 * 2 * 1000;
const fiveSeconds = 5 * 1000;
const fifteenMinutes = 15 * 60 * 1000;
// let timeoutId: string | number | NodeJS.Timeout | null | undefined = null;

export function useSubmissionState({
  documentId,
  enabled,
  submissionInfo,
  voucher,
  checkFailed = false,
  currencyCode,
  isPaymentCompleted,
  isPaymentTypeQR,
}: {
  documentId: DocumentId;
  enabled: boolean;
  submissionInfo: ExtendedSubmissionInfo;
  voucher: Voucher | null;
  checkFailed?: boolean;
  currencyCode?: string;
  isPaymentCompleted?: boolean;
  isPaymentTypeQR?: boolean;
}): { submissionState: SubmissionState; refetchSubmissionState: () => void } {
  const rum = useRum();
  const getSubmissionState = (
    submissionInfo: ExtendedSubmissionInfo,
  ): SubmissionState => {
    if (checkFailed) {
      return {
        name: 'PROCESSING_FAILED',
      };
    }

    if (submissionInfo.status === 'SAMPLE') {
      return {
        name: 'SAMPLE',
        finalPrice: submissionInfo.finalPrice,
        currency: submissionInfo.currency,
        fileUploadDate: submissionInfo.fileUploadDate,
        tittle: submissionInfo?.tittle ?? '',
        salePrice: submissionInfo?.salePrice ?? '',
        discountPrice: submissionInfo?.discountPrice ?? '',
        salePriceOriginal: submissionInfo?.salePriceOriginal ?? 0,
        finalPriceOriginal: submissionInfo?.finalPriceOriginal ?? 0,
        discountApplied: submissionInfo?.discountApplied ?? false,
        orderId: submissionInfo?.orderId ?? '',
        addOnServiceList: submissionInfo?.addOnServiceList ?? [],
        peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? undefined,
      };
    }

    if (submissionInfo.status === 'REQUIRES_PAYMENT') {
      if (voucher?.voucherStatus === 'VALID') {
        return {
          name: 'VOUCHER_READY',
          editedFileName: submissionInfo.editedFileName,
          voucherCode: voucher.code,
          fileUploadDate: submissionInfo.fileUploadDate,
          tittle: submissionInfo?.tittle ?? '',
        };
      } else {
        return {
          name: 'REQUIRES_PAYMENT',
          editedFileName: submissionInfo.editedFileName,
          finalPrice: submissionInfo.finalPrice,
          paymentStatus: submissionInfo.paymentStatus,
          currency: submissionInfo.currency,
          fileUploadDate: submissionInfo.fileUploadDate,
          tittle: submissionInfo?.tittle ?? '',
          salePrice: submissionInfo?.salePrice ?? '',
          salePriceOriginal: submissionInfo?.salePriceOriginal ?? 0,
          finalPriceOriginal: submissionInfo?.finalPriceOriginal ?? 0,
          discountPrice: submissionInfo?.discountPrice ?? '',
          discountApplied: submissionInfo?.discountApplied ?? false,
          orderId: submissionInfo?.orderId ?? '',
          addOnServiceList: submissionInfo?.addOnServiceList ?? [],
          peerPilotEligibility:
            submissionInfo?.peerPilotEligibility ?? undefined,
        };
      }
    }

    if (submissionInfo.status === 'EXPIRED') {
      return {
        name: 'EXPIRED',
        downloadAvailableDays: submissionInfo.downloadAvailableDays,
        fileUploadDate: submissionInfo.fileUploadDate,
        tittle: submissionInfo?.tittle ?? '',
      };
    }

    if (submissionInfo.status === 'PROCESSING_FAILED') {
      rum.pushError(
        new Error(
          `Submission info endpoint returned an error status - ${submissionInfo.status}`,
        ),
        {
          type: 'Generic Error',
          context: {
            errorName: 'Submission info endpoint returned an error status',
            documentId: documentId,
            status: submissionInfo.status,
          },
        },
      );
      return {
        name: submissionInfo.status,
      };
    }

    if (submissionInfo.status === 'ERROR') {
      rum.pushError(
        new Error(
          `Submission info endpoint returned an error status - ${submissionInfo.status}`,
        ),
        {
          type: 'Generic Error',
          context: {
            errorName: 'Submission info endpoint returned an error status',
            documentId: documentId,
            status: submissionInfo.status,
          },
        },
      );
      return {
        name: 'ERROR',
      };
    }

    if (submissionInfo.status === 'REQUIRES_DOCUMENT') {
      if (voucher?.voucherStatus === 'VALID') {
        return {
          name: 'VOUCHER_PENDING',
          generatingDocument: enabled,
          fileUploadDate: submissionInfo.fileUploadDate,
          tittle: submissionInfo?.tittle ?? '',
        };
      } else {
        return {
          name: 'UNKNOWN',
          generatingDocument: enabled,
          finalPrice: submissionInfo.finalPrice,
          currency: submissionInfo.currency,
          fileUploadDate: submissionInfo.fileUploadDate,
          tittle: submissionInfo?.tittle ?? '',
          salePrice: submissionInfo?.salePrice ?? '',
          salePriceOriginal: submissionInfo?.salePriceOriginal ?? 0,
          finalPriceOriginal: submissionInfo?.finalPriceOriginal ?? 0,
          discountPrice: submissionInfo?.discountPrice ?? '',
          discountApplied: submissionInfo?.discountApplied ?? false,
          orderId: submissionInfo?.orderId ?? '',
          addOnServiceList: submissionInfo?.addOnServiceList ?? [],
          peerPilotEligibility:
            submissionInfo?.peerPilotEligibility ?? undefined,
        };
      }
    }

    if (submissionInfo.status === 'NO_ISSUES') {
      return {
        name: 'NO_ISSUES',
        tittle: submissionInfo?.tittle ?? '',
        editedFileName: submissionInfo?.editedFileName ?? '',
      };
    }

    return {
      name: submissionInfo.status,
      downloadAvailableDays: submissionInfo.downloadAvailableDays,
      downloadState: submissionInfo.downloadState,
      editedFileName: submissionInfo.editedFileName,
      fileUploadDate: submissionInfo.fileUploadDate,
      finalPrice: submissionInfo?.finalPrice ?? '',
      currency: submissionInfo?.currency ?? '',
      tittle: submissionInfo?.tittle ?? '',
      salePrice: submissionInfo?.salePrice ?? '',
      salePriceOriginal: submissionInfo?.salePriceOriginal ?? 0,
      finalPriceOriginal: submissionInfo?.finalPriceOriginal ?? 0,
      discountPrice: submissionInfo?.discountPrice ?? '',
      discountApplied: submissionInfo?.discountApplied ?? false,
      orderId: submissionInfo?.orderId ?? '',
      addOnServiceList: submissionInfo?.addOnServiceList ?? [],
      peerPilotEligibility: submissionInfo?.peerPilotEligibility ?? undefined,
    };
  };

  const initialSubmissionState: SubmissionState =
    getSubmissionState(submissionInfo);

  const [shouldRefetch, setShouldRefetch] = useState(
    shouldAutoRefetch(
      initialSubmissionState,
      isPaymentCompleted,
      isPaymentTypeQR,
    ),
  );

  const [shouldStop, setShouldStop] = useState(false);
  const [checkFailedState, setCheckFailedState] = useState(false);
  const [submissionState, setSubmissionState] = useState<SubmissionState>(
    initialSubmissionState,
  );

  useEffect(() => {
    if (isPaymentTypeQR) {
      setShouldStop(false);
      setShouldRefetch(
        shouldAutoRefetch(
          initialSubmissionState,
          isPaymentCompleted,
          isPaymentTypeQR,
        ),
      );
    }
  }, [isPaymentTypeQR]);

  if (checkFailed && !checkFailedState) {
    setSubmissionState(initialSubmissionState);
    setCheckFailedState(true);
  }

  useQuery({
    queryKey: [
      'submissionState',
      documentId,
      process.env.NEXT_PUBLIC_API_BASE_URL,
      preflightCoreApiClient,
      currencyCode,
    ],
    queryFn: () =>
      getExtendedSubmissionInfo({
        documentId,
        isSampleDocument: false,
        preflightCoreApiClient,
        currencyCode,
      }),

    enabled,
    onSuccess: (submissionInfo) => {
      const newSubmissionState: SubmissionState =
        getSubmissionState(submissionInfo);

      setShouldRefetch(
        shouldAutoRefetch(
          newSubmissionState,
          isPaymentCompleted,
          isPaymentTypeQR,
        ),
      );

      setSubmissionState(newSubmissionState);
    },
    refetchInterval:
      shouldRefetch && !shouldStop
        ? isPaymentTypeQR
          ? fiveSeconds
          : 1000
        : false,
    onSettled: () => {
      // Clear the old timeout
      // if (timeoutId) {
      //   clearTimeout(timeoutId);
      // }
      setTimeout(
        () => {
          setShouldStop(true);
        },
        isPaymentCompleted
          ? fiveSeconds
          : isPaymentTypeQR
          ? fifteenMinutes
          : twoMinutes,
      );
    },
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    onError: (error) => {
      rum.pushError(
        new Error(
          `Submission info endpoint returned an error status - ${JSON.stringify(
            error,
          )}`,
        ),
        {
          type: 'Generic Error',
          context: {
            errorName: 'Submission info endpoint returned an error status',
          },
        },
      );
    },
  });

  return {
    submissionState,
    refetchSubmissionState: () => {
      if (
        !isFinal[submissionState.name] &&
        submissionState.name !== 'PROCESSING_FAILED_RETRYING' &&
        submissionState.name !== 'ERROR_RETRYING'
      ) {
        if (submissionState.name === 'ERROR') {
          setSubmissionState({ name: 'ERROR_RETRYING' });
        }

        if (submissionState.name === 'PROCESSING_FAILED') {
          setSubmissionState({ name: 'PROCESSING_FAILED_RETRYING' });
        }

        setShouldRefetch(true);
      }
    },
  };
}

const isFinal: Record<SubmissionState['name'], boolean> = {
  UNKNOWN: false,
  VOUCHER_PENDING: false,
  PROCESSING_FAILED: false,
  PROCESSING_FAILED_RETRYING: false,
  REQUIRES_PAYMENT: false,
  VOUCHER_READY: false,
  VOUCHER_PAID: true,
  CREDIT_CARD_PAID: true,
  FREE: true,
  NO_ISSUES: true,
  REFUNDED: true,
  EXPIRED: true,
  ERROR: false,
  ERROR_RETRYING: false,
  SAMPLE: true,
  REUPLOAD_OF_PURCHASED_DOCUMENT: true,
  UNPAID: false,
};

function shouldAutoRefetch(
  submissionState: SubmissionState,
  isPaymentCompleted?: boolean,
  isPaymentTypeQR?: boolean,
): boolean {
  if (
    submissionState.name === 'UNKNOWN' ||
    submissionState.name === 'VOUCHER_PENDING'
  ) {
    return true;
  }

  if (
    submissionState.name === 'REQUIRES_PAYMENT' &&
    submissionState.paymentStatus === 'REUPLOAD_OF_PURCHASED_DOCUMENT'
  ) {
    return true;
  }

  if (isPaymentTypeQR && submissionState.name !== 'CREDIT_CARD_PAID') {
    return true;
  }

  if (isPaymentCompleted && submissionState.name !== 'CREDIT_CARD_PAID') {
    return true;
  }

  if (
    submissionState.name === 'CREDIT_CARD_PAID' ||
    submissionState.name === 'FREE' ||
    submissionState.name === 'VOUCHER_PAID' ||
    submissionState.name === 'REFUNDED' ||
    submissionState.name === 'REUPLOAD_OF_PURCHASED_DOCUMENT'
  ) {
    return submissionState.downloadState === 'PENDING';
  }

  return false;
}
